import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Globe, Server, CheckCircle, X, Building2, Phone, Mail,
  MapPin, DollarSign, Palette, Image, Info, ArrowRight,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Template types with recommended pricing
const TEMPLATE_TYPES = {
  peluqueria: { label: 'Peluquería', icon: '💇', basePrice: 25 },
  estetica: { label: 'Centro de Estética', icon: '💆', basePrice: 30 },
  limpieza: { label: 'Limpieza', icon: '🧹', basePrice: 25 },
  fontanero: { label: 'Fontanería', icon: '🔧', basePrice: 30 },
  electricista: { label: 'Electricista', icon: '⚡', basePrice: 30 },
  cerrajero: { label: 'Cerrajería', icon: '🔑', basePrice: 25 },
  taller: { label: 'Taller Mecánico', icon: '🚗', basePrice: 35 },
  detailing: { label: 'Car Detailing', icon: '✨', basePrice: 35 },
  electrodomesticos: { label: 'Electrodomésticos', icon: '🔌', basePrice: 30 },
  panaderia: { label: 'Panadería', icon: '🥖', basePrice: 25 },
  humedades: { label: 'Eliminación Humedades', icon: '💧', basePrice: 50, highTicket: true },
  jardineria: { label: 'Jardinería', icon: '🌿', basePrice: 45, highTicket: true },
  reformas: { label: 'Reformas Integrales', icon: '🏗️', basePrice: 60, highTicket: true },
  climatizacion: { label: 'Climatización', icon: '❄️', basePrice: 55, highTicket: true },
  piscinas: { label: 'Piscinas', icon: '🏊', basePrice: 50, highTicket: true }
};

export function CreateClientSiteModal({ solicitud, adminId, subadminId, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    // Hosting
    hosting_type: 'subdomain',
    subdomain: solicitud?.business_name?.toLowerCase().replace(/[^a-z0-9]/g, '-').substring(0, 20) || '',
    custom_domain: '',
    
    // Template
    template_type: solicitud?.business_type || 'otro',
    
    // Business Info
    business_name: solicitud?.business_name || '',
    business_phone: solicitud?.phone || '',
    business_email: solicitud?.email || '',
    business_address: '',
    business_city: '',
    business_slogan: '',
    business_description: '',
    
    // Social
    whatsapp_number: solicitud?.phone || '',
    facebook_url: '',
    instagram_url: '',
    
    // Images
    logo_url: '',
    hero_image_url: '',
    
    // Theme
    primary_color: '#10B981',
    secondary_color: '#1F2937',
    
    // Maintenance
    monthly_price: TEMPLATE_TYPES[solicitud?.business_type]?.basePrice || 30
  });

  const headers = subadminId 
    ? { 'X-SubAdmin-Id': subadminId }
    : { 'X-Admin-Id': adminId };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      
      const siteData = {
        solicitud_id: solicitud.id,
        hosting_type: form.hosting_type,
        subdomain: form.hosting_type === 'subdomain' ? form.subdomain : null,
        custom_domain: form.hosting_type === 'custom_domain' ? form.custom_domain : null,
        template_type: form.template_type,
        
        business_info: {
          name: form.business_name,
          phone: form.business_phone,
          email: form.business_email,
          address: form.business_address,
          city: form.business_city,
          slogan: form.business_slogan,
          description: form.business_description,
          whatsapp_number: form.whatsapp_number,
          facebook_url: form.facebook_url || null,
          instagram_url: form.instagram_url || null
        },
        
        theme: {
          primary_color: form.primary_color,
          secondary_color: form.secondary_color,
          accent_color: '#F59E0B',
          background_color: '#111827',
          text_color: '#FFFFFF'
        },
        
        images: {
          logo_url: form.logo_url || null,
          hero_image_url: form.hero_image_url || null,
          gallery_urls: []
        },
        
        maintenance_plan: {
          monthly_price: parseFloat(form.monthly_price) || 0,
          includes_hosting: true,
          includes_ssl: true,
          includes_updates: 2,
          includes_support_hours: 1.0,
          extra_update_price: 25,
          extra_support_hour_price: 30
        }
      };

      const response = await axios.post(`${API_URL}/api/client-sites`, siteData, { headers });
      
      if (response.data.success) {
        toast.success('Sitio creado correctamente');
        onSuccess?.(response.data.site);
        onClose();
      }
    } catch (error) {
      console.error('Error creating site:', error);
      toast.error(error.response?.data?.detail || 'Error al crear sitio');
    } finally {
      setSaving(false);
    }
  };

  const templateConfig = TEMPLATE_TYPES[form.template_type] || { label: 'Otro', icon: '📊', basePrice: 30 };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-gray-700"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-900/50 to-emerald-900/50 border-b border-gray-800 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Server className="w-5 h-5 text-cyan-400" />
                Desplegar Sitio Web
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                Convierte la solicitud de "{solicitud?.business_name}" en un sitio activo
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? 'bg-cyan-500 text-white' : 'bg-gray-700 text-gray-400'
                }`}>
                  {s}
                </div>
                {s < 3 && <div className={`w-8 h-0.5 ${step > s ? 'bg-cyan-500' : 'bg-gray-700'}`} />}
              </div>
            ))}
            <span className="ml-2 text-sm text-gray-400">
              {step === 1 ? 'Hosting' : step === 2 ? 'Información' : 'Finalizar'}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Step 1: Hosting */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h4 className="text-white font-medium mb-4">Tipo de Hosting</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, hosting_type: 'subdomain' }))}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      form.hosting_type === 'subdomain'
                        ? 'border-cyan-500 bg-cyan-900/20'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">🌐</span>
                      <div>
                        <p className="font-medium text-white">Subdominio LokAlGrow</p>
                        <p className="text-xs text-cyan-400">Precio reducido</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-400">
                      El sitio estará en: <strong className="text-white">{form.subdomain || 'cliente'}.lokalgrow.com</strong>
                    </p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, hosting_type: 'custom_domain' }))}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      form.hosting_type === 'custom_domain'
                        ? 'border-purple-500 bg-purple-900/20'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">🔗</span>
                      <div>
                        <p className="font-medium text-white">Dominio Propio</p>
                        <p className="text-xs text-purple-400">Precio premium</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-400">
                      El cliente usa su propio dominio (requiere configuración DNS)
                    </p>
                  </button>
                </div>
              </div>

              {form.hosting_type === 'subdomain' ? (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Nombre del subdominio</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={form.subdomain}
                      onChange={(e) => setForm(f => ({ 
                        ...f, 
                        subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') 
                      }))}
                      className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                      placeholder="mi-negocio"
                      data-testid="subdomain-input"
                    />
                    <span className="text-gray-400">.lokalgrow.com</span>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Dominio del cliente</label>
                  <input
                    type="text"
                    value={form.custom_domain}
                    onChange={(e) => setForm(f => ({ ...f, custom_domain: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                    placeholder="www.mi-negocio.es"
                    data-testid="custom-domain-input"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    💡 El cliente deberá apuntar su dominio a nuestros servidores (te daremos las instrucciones)
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm text-gray-400 mb-2">Plantilla / Sector</label>
                <select
                  value={form.template_type}
                  onChange={(e) => {
                    const type = e.target.value;
                    const config = TEMPLATE_TYPES[type] || { basePrice: 30 };
                    setForm(f => ({ 
                      ...f, 
                      template_type: type,
                      monthly_price: form.hosting_type === 'custom_domain' 
                        ? config.basePrice * 2 
                        : config.basePrice
                    }));
                  }}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                >
                  {Object.entries(TEMPLATE_TYPES).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.icon} {config.label} {config.highTicket ? '⭐' : ''}
                    </option>
                  ))}
                  <option value="otro">📊 Otro</option>
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Business Info */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Nombre del Negocio *</label>
                  <input
                    type="text"
                    value={form.business_name}
                    onChange={(e) => setForm(f => ({ ...f, business_name: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                    data-testid="create-business-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Teléfono *</label>
                  <input
                    type="tel"
                    value={form.business_phone}
                    onChange={(e) => setForm(f => ({ ...f, business_phone: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Email *</label>
                  <input
                    type="email"
                    value={form.business_email}
                    onChange={(e) => setForm(f => ({ ...f, business_email: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Ciudad</label>
                  <input
                    type="text"
                    value={form.business_city}
                    onChange={(e) => setForm(f => ({ ...f, business_city: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">Dirección</label>
                  <input
                    type="text"
                    value={form.business_address}
                    onChange={(e) => setForm(f => ({ ...f, business_address: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">Eslogan</label>
                  <input
                    type="text"
                    value={form.business_slogan}
                    onChange={(e) => setForm(f => ({ ...f, business_slogan: e.target.value }))}
                    placeholder="Tu especialista en..."
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>

              <div>
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Image className="w-4 h-4 text-gray-400" />
                  Imágenes (URLs)
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">URL del Logo (Google Drive, etc.)</label>
                    <input
                      type="url"
                      value={form.logo_url}
                      onChange={(e) => setForm(f => ({ ...f, logo_url: e.target.value }))}
                      placeholder="https://drive.google.com/..."
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">URL Imagen Principal</label>
                    <input
                      type="url"
                      value={form.hero_image_url}
                      onChange={(e) => setForm(f => ({ ...f, hero_image_url: e.target.value }))}
                      placeholder="https://drive.google.com/..."
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Palette className="w-4 h-4 text-gray-400" />
                  Colores
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Color Principal</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={form.primary_color}
                        onChange={(e) => setForm(f => ({ ...f, primary_color: e.target.value }))}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={form.primary_color}
                        onChange={(e) => setForm(f => ({ ...f, primary_color: e.target.value }))}
                        className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Color Secundario</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={form.secondary_color}
                        onChange={(e) => setForm(f => ({ ...f, secondary_color: e.target.value }))}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={form.secondary_color}
                        onChange={(e) => setForm(f => ({ ...f, secondary_color: e.target.value }))}
                        className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Pricing & Confirm */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-xl p-6 border border-green-700/30">
                <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  Plan de Mantenimiento Mensual
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Precio Mensual (€)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.monthly_price}
                      onChange={(e) => setForm(f => ({ ...f, monthly_price: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-xl font-bold focus:ring-2 focus:ring-green-500"
                      data-testid="create-monthly-price"
                    />
                  </div>
                  
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <p className="text-sm text-gray-300 mb-2">💡 Precios recomendados:</p>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>• <strong className="text-cyan-400">Subdominio:</strong> {templateConfig.basePrice}€ - {templateConfig.basePrice + 15}€/mes</li>
                      <li>• <strong className="text-purple-400">Dominio propio:</strong> {templateConfig.basePrice * 2}€ - {templateConfig.basePrice * 2 + 30}€/mes</li>
                      {templateConfig.highTicket && (
                        <li className="text-amber-400">⭐ Sector High Ticket: considera +20-30% adicional</li>
                      )}
                    </ul>
                  </div>

                  <div className="text-sm text-gray-400">
                    <p className="font-medium text-white mb-2">El plan incluye:</p>
                    <ul className="space-y-1">
                      <li>✅ Hosting incluido</li>
                      <li>✅ Certificado SSL</li>
                      <li>✅ 2 actualizaciones mensuales</li>
                      <li>✅ 1 hora de soporte</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <h4 className="text-white font-medium mb-3">Resumen del Sitio</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Negocio:</span>
                    <p className="text-white">{form.business_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Hosting:</span>
                    <p className="text-white">
                      {form.hosting_type === 'subdomain' 
                        ? `${form.subdomain}.lokalgrow.com` 
                        : form.custom_domain}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Plantilla:</span>
                    <p className="text-white">{templateConfig.icon} {templateConfig.label}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Precio mensual:</span>
                    <p className="text-green-400 font-bold">{form.monthly_price}€/mes</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-800 p-4 flex items-center justify-between bg-gray-900">
          <Button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            variant="outline"
          >
            {step > 1 ? 'Anterior' : 'Cancelar'}
          </Button>
          
          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              className="bg-cyan-600 hover:bg-cyan-700"
              disabled={
                (step === 1 && form.hosting_type === 'subdomain' && !form.subdomain) ||
                (step === 1 && form.hosting_type === 'custom_domain' && !form.custom_domain)
              }
            >
              Siguiente
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={saving || !form.business_name || !form.business_phone || !form.business_email}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="create-site-submit"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Crear Sitio
            </Button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default CreateClientSiteModal;
